import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { Field, Form } from 'react-final-form'
import {
  Button,
  IconButton,
  FormControl,
  FormControlLabel,
  MenuItem,
  Typography,
  InputLabel,
  TextField,
} from '@material-ui/core'
import IconBack from '@material-ui/icons/ArrowBack'
import IconUploadFile from '@material-ui/icons/CloudUpload'
import ClearIcon from '@material-ui/icons/Clear'
import { Checkbox } from 'components/common/FormInputs'
import withPromise from 'helpers/withPromise'
import Input from 'components/common/Form/Input'
import SelectInput from 'components/common/Form/SelectInput'
import TextEditor from 'components/common/TextEditor'
import getValidator, {
  VALIDATOR_IS_EMPTY,
  VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
  VALIDATOR_IS_YOUTUBE_LINK,
} from 'validation'
import {
  jobInputGroupsClearDetails,
  saveJobInputGroupsInfo,
  fetchJobWorkflowInputGroupItem,
} from 'reducers/jobInputGroupsAdmin/reducer'
import {
  fetchJobWorkflows,
  fetchJobWorkflowTasks,
} from 'reducers/jobInputsAdmin/reducer'
import Psfs from 'lib/psfs/Psfs'
import { APP_LANGUAGES } from 'constants/index'
import TitleInput from 'components/common/TitleInput/TitleInput'
import TextArea from 'components/common/Form/TextArea'
import { MY_JOBS_UPLOAD_FILES } from 'constants/apiRouting'
import NoticeHelper from 'helpers/notice'
import { uploadFiles } from 'reducers/common/files/reducer'
import { showNotice } from 'reducers/notice'
import messages from '../translate'

function JobInputGroupItem({
  jobWorkflows,
  jobWorkflowTasks,
  formData,
  saveJobInputGroupsInfo,
  fetchJobWorkflows,
  fetchJobWorkflowTasks,
  fetchJobWorkflowInputGroupItem,
  jobInputGroupsClearDetails,
  history: { goBack },
  location: { search },
  intl: { formatMessage },
  match: {
    params: { id },
  },
  uploadFiles,
  featureFlags,
}) {
  const getEntityId = () => (id !== 'create' ? id : false)

  const [redirect, setRedirect] = useState(false)
  const [jobWorkflow, setJobWorkflow] = useState(null)
  const [titleChanged, setTitleChanged] = useState(false)
  const initialLanguages = {}
  const [formState, setFormState] = useState({
    title: initialLanguages,
    workflow: null,
    task: null,
    position: 0,
    duplicable: true,
    enabled: true,
    instructions: initialLanguages,
    image_path: '',
    youtube_video_link: '',
  })

  Object.values(APP_LANGUAGES).forEach(({ key }) => {
    initialLanguages[key] = ''
  })

  useEffect(
    () => () => {
      jobInputGroupsClearDetails()
    },
    [],
  )

  useEffect(() => {
    const entityId = getEntityId()
    const psfs = new Psfs()
    psfs.addSort('title')

    fetchJobWorkflows({ psfs })

    if (entityId) {
      fetchJobWorkflowInputGroupItem({ entityId })
    }
  }, [])

  useEffect(() => {
    if (jobWorkflow) {
      const psfs = new Psfs()
      psfs.addFilter('workflow', jobWorkflow).addSort('title')

      fetchJobWorkflowTasks({ psfs })
    }
  }, [jobWorkflow])

  useEffect(() => {
    if (redirect) {
      goBack()
    }
  }, [redirect, goBack])

  useEffect(() => {
    if (jobWorkflows.size && search && search.length) {
      const queryParams = new URLSearchParams(search)
      const selectedJob = jobWorkflows.find(
        ({ title }) => title === queryParams.get('jobTitle'),
      )

      setFormState(prevState => ({
        ...prevState,
        workflow: selectedJob.id,
      }))
      setJobWorkflow(selectedJob.id)

      if (jobWorkflowTasks.size) {
        const selectedTask = jobWorkflowTasks.find(
          ({ title }) => title === queryParams.get('taskTitle'),
        )

        setFormState(prevState => ({
          ...prevState,
          task: selectedTask.id,
        }))
      }
    }
  }, [jobWorkflows.size, jobWorkflowTasks.size])

  const [imageData, setImageData] = useState({})
  const [imagePath, setImagePath] = useState('')

  useEffect(() => {
    if (Object.keys(imageData).length) {
      setImagePath(imageData.path)
    }
  }, [imageData])

  const onChangeFile = file => {
    if (!file) return

    const fileForm = new FormData()
    fileForm.append('file', file[0])

    withPromise(uploadFiles, {
      file: fileForm,
      partUrl: MY_JOBS_UPLOAD_FILES,
      formatMessage,
    }).then(response => {
      if (response.error) {
        showNotice({
          notice: NoticeHelper.createErrorObj(
            formatMessage(messages.fileNotUploadMessage),
          ),
        })
      } else {
        setImageData(prevState => ({
          ...prevState,
          ...response,
        }))
      }
    })
  }

  const removeImage = () => {
    setImageData({})
    setImagePath('')
    onChangeFile(null)
  }

  useEffect(() => {
    if (formData.size) {
      const {
        title,
        position,
        enabled,
        duplicable,
        instructions,
        image_path,
        youtube_video_link,
        task: { id: taskId },
        workflow: { id: workflowId },
      } = formData.toJS()

      setJobWorkflow(workflowId)
      setImagePath(image_path)
      setFormState(prevState => ({
        ...prevState,
        title,
        position,
        enabled,
        duplicable,
        instructions,
        image_path,
        youtube_video_link,
        task: taskId,
        workflow: workflowId,
      }))
    }
  }, [formData])

  const handleSubmitForm = ({ type, ...restValues }) => {
    const valueObj = { ...restValues }

    if (!getEntityId()) {
      valueObj.type = type
    }

    Object.values(APP_LANGUAGES).forEach(({ key }) => {
      if (!valueObj.title[key]) {
        valueObj.title[key] = valueObj.title.en
      }

      if (valueObj.instructions.en) {
        if (!valueObj.instructions[key]) {
          valueObj.instructions[key] = valueObj.instructions.en
        }
      } else {
        valueObj.instructions[key] = null
      }
    })

    valueObj.image_path = imagePath

    return withPromise(saveJobInputGroupsInfo, {
      entityId: getEntityId(),
      data: valueObj,
    }).then(errors => {
      if (errors) {
        return Promise.resolve(errors)
      }

      setRedirect(true)
    })
  }

  return (
    <div className="container-fluid no-breadcrumb">
      <Typography variant="h5" className="headline">
        <IconButton size="medium" className="btn-back" onClick={goBack}>
          <IconBack />
        </IconButton>
        {formatMessage(messages.jobInputGroupsInfo)}
      </Typography>
      <Form
        initialValues={formState}
        onSubmit={handleSubmitForm}
        {...(featureFlags.INSTRUCTIONS_TEXT_EDITOR
          ? { subscription: { submitting: true, touched: true, invalid: true } }
          : {})}
        mutators={{
          handleWorkflowChange: (
            [
              {
                target: { value },
              },
            ],
            state,
            { changeValue },
          ) => {
            setJobWorkflow(value)
            changeValue(state, 'workflow', () => value)
            changeValue(state, 'task', () => null)
          },
          handleTaskChange: (
            [
              {
                target: { value },
              },
            ],
            state,
            { changeValue },
          ) => {
            changeValue(state, 'task', () => value)
          },
          handleTitleClear: (value, state, { changeValue }) => {
            changeValue(state, `title.${value[0]}`, () => {})
            setTitleChanged(true)
          },
          handleInstructionsClear: (value, state, { changeValue }) => {
            changeValue(state, `instructions.${value[0]}`, () => {})
            setTitleChanged(true)
          },
        }}
        render={({
          handleSubmit,
          submitting,
          pristine,
          form: { mutators },
        }) => (
          <form onSubmit={handleSubmit} className="form-basic">
            <TitleInput
              handleTitleClear={mutators.handleTitleClear}
              formState={formState.title}>
              {Object.values(APP_LANGUAGES).map(({ key }) => {
                const isEnglish = key === 'en'
                const validators = [VALIDATOR_IS_LESS_THEN_255_SYMBOLS]

                if (isEnglish) {
                  validators.push(VALIDATOR_IS_EMPTY)
                }

                return (
                  <Field
                    required={isEnglish}
                    name={`title[${key}]`}
                    key={`${key}title`}
                    lang={key}
                    type="text"
                    label={`${key} ${formatMessage(messages.title)}`}
                    component={Input}
                    className="form-control"
                    validate={getValidator(validators)}
                  />
                )
              })}
            </TitleInput>
            <Field
              required
              name="workflow"
              label={formatMessage(messages.jobWorkflow)}
              component={SelectInput}
              disabled={submitting}
              onChange={(args, state, utils) => {
                mutators.handleWorkflowChange(args, state, utils)
              }}
              validate={getValidator(VALIDATOR_IS_EMPTY)}>
              {jobWorkflows.map(({ id: jobId, title: jobTitle }) => (
                <MenuItem button key={jobId} value={jobId}>
                  {jobTitle}
                </MenuItem>
              ))}
            </Field>
            <Field
              required
              name="task"
              label={formatMessage(messages.jobTaskWorkflow)}
              component={SelectInput}
              disabled={submitting || !jobWorkflowTasks.size}
              validate={getValidator(VALIDATOR_IS_EMPTY)}
              onChange={(args, state, utils) => {
                mutators.handleTaskChange(args, state, utils)
              }}>
              {jobWorkflowTasks.map(
                ({ id: jobTaskId, title: jobTaskTitle }) => (
                  <MenuItem button key={jobTaskId} value={jobTaskId}>
                    {jobTaskTitle}
                  </MenuItem>
                ),
              )}
            </Field>
            <TitleInput
              handleTitleClear={mutators.handleInstructionsClear}
              formState={formState.instructions}>
              {Object.values(APP_LANGUAGES).map(({ key }) => (
                <Field
                  name={`instructions[${key}]`}
                  key={`${key}_instructions`}
                  type="text"
                  lang={key}
                  label={`${key} ${formatMessage(messages.instructions)}`}
                  component={
                    featureFlags.INSTRUCTIONS_TEXT_EDITOR
                      ? props => (
                          <TextEditor
                            placeholder={`${key} ${formatMessage(
                              messages.instructions,
                            )}`}
                            {...props}
                          />
                        )
                      : TextArea
                  }
                  className="form-control"
                  disabled={submitting}
                />
              ))}
            </TitleInput>
            <InputLabel htmlFor="filePicker">
              <Field
                name="image_path"
                label="image"
                className="form-control"
                disabled={submitting}>
                {({ input: { value, onChange, ...input } }) => (
                  <input
                    {...input}
                    className="file_input"
                    id="filePicker"
                    type="file"
                    name="image_path"
                    accept="image/jpg, image/jpeg, image/png"
                    onChange={({ target: { files } }) => onChangeFile(files)}
                  />
                )}
              </Field>
              {!imagePath && (
                <div className="field_input">
                  <IconUploadFile className="field_icon" />
                  {formatMessage({ id: 'upload_png_jpg' })}
                </div>
              )}
            </InputLabel>
            {imagePath && (
              <div className="form-group image_wrap">
                <TextField
                  fullWidth
                  readOnly
                  type="text"
                  label={formatMessage({ id: 'image_path' })}
                  value={imagePath}
                />
                <ClearIcon className="remove_image" onClick={removeImage} />
              </div>
            )}
            <Field
              name="youtube_video_link"
              type="text"
              label={formatMessage({ id: 'youtube_video_link' })}
              component={Input}
              className="form-control"
              disabled={submitting}
              validate={getValidator(VALIDATOR_IS_YOUTUBE_LINK)}
            />
            <Field
              required
              name="position"
              type="number"
              label={formatMessage(messages.position)}
              component={Input}
              disabled={submitting}
              parse={val => {
                if (val === '') {
                  return val
                }

                return +val
              }}
              validate={getValidator(VALIDATOR_IS_EMPTY)}
            />
            <div className="form-group">
              <FormControl required>
                <FormControlLabel
                  label={formatMessage(messages.active)}
                  disabled={submitting}
                  className="form-label"
                  control={
                    <Field
                      name="enabled"
                      component={Checkbox}
                      type="checkbox"
                      color="primary"
                      disabled={submitting}
                    />
                  }
                />
              </FormControl>
            </div>
            <div className="form-group">
              <FormControl required>
                <FormControlLabel
                  label={formatMessage(messages.duplicable)}
                  disabled={submitting}
                  className="form-label"
                  control={
                    <Field
                      name="duplicable"
                      component={Checkbox}
                      type="checkbox"
                      color="primary"
                      disabled={submitting}
                    />
                  }
                />
              </FormControl>
            </div>
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={(submitting || pristine) && !titleChanged}>
              {formatMessage(messages.save)}
            </Button>
          </form>
        )}
      />
    </div>
  )
}

JobInputGroupItem.propTypes = {
  formData: PropTypes.object.isRequired,
  jobWorkflows: PropTypes.object.isRequired,
  jobWorkflowTasks: PropTypes.object.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  saveJobInputGroupsInfo: PropTypes.func,
  jobInputGroupsClearDetails: PropTypes.func,
  fetchJobWorkflows: PropTypes.func,
  fetchJobWorkflowTasks: PropTypes.func,
  fetchJobWorkflowInputGroupItem: PropTypes.func,
}

JobInputGroupItem.defaultProps = {
  saveJobInputGroupsInfo: () => {},
  jobInputGroupsClearDetails: () => {},
  fetchJobWorkflows: () => {},
  fetchJobWorkflowTasks: () => {},
  fetchJobWorkflowInputGroupItem: () => {},
}

export default connect(
  state => ({
    formData: state.getIn(['jobInputGroupsAdmin', 'formData']),
    jobWorkflows: state.getIn(['jobInputsAdmin', 'jobWorkflows']),
    jobWorkflowTasks: state.getIn(['jobInputsAdmin', 'jobWorkflowTasks']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
  }),
  {
    saveJobInputGroupsInfo,
    jobInputGroupsClearDetails,
    fetchJobWorkflows,
    fetchJobWorkflowTasks,
    fetchJobWorkflowInputGroupItem,
    uploadFiles,
    showNotice,
  },
)(injectIntl(JobInputGroupItem))
